/**
 * Division page
 */
import React, {useState, useEffect} from "react";
import {useParams, useNavigate} from "react-router-dom";
import Container from "./../components/core/Container";
import DivisionBanner from "../components/division/DivisionBanner";
import DivisionSchedule from "../components/division/DivisionSchedule";
import DivisionContent from "../components/division/DivisionContent";
import DivisionStatistics from "../components/division/DivisionStatistics";
import DivisionStandings from "../components/division/DivisionStandings";
import PowerRankings from "../components/division/DivisionPowerRankings";
import AllStars from "../components/division/DivisionAllStars";
import DivisionSidebar from "../components/division/DivisionSidebar";
import {useQuery} from "@tanstack/react-query";
import {Helmet} from "react-helmet-async";
import Loading from "../components/core/Loading";
import PageSidebarContainer from "../components/core/PageSidebarContainer";
import ContentContainer from "../components/core/ContentContainer";
import DivisionTournament from "../components/division/DivisionTournament";

const Division = () => {
    const {divisionId, tab} = useParams(); // Get divisionId and tab from URL params
    const navigate = useNavigate(); // Initialize navigate for updating URL
    const [activeTab, setActiveTab] = useState(tab || "standings"); // Default to 'standings' tab if no tab is specified
    const [tabContents, setTabContents] = useState(null); // Initialize tabContents as null

    const processDivisionData = (jsonData) => {
        return jsonData; // Process or map the fetched division data as needed
    };
    // Fetch the division data using react-query
    const {isLoading, error, data} = useQuery({
        staleTime: 3600000, // Cache for 1 hour
        queryKey: ["division", divisionId],
        queryFn: () =>
            fetch(`${process.env.REACT_APP_BUIHA_API_URL}/division/${divisionId}/`)
                .then((res) => res.json())
                .then((json) => {
                    return processDivisionData(json);
                }),
    });

    // Handle tab click and update the URL accordingly
    const handleTabClick = (tab) => {
        setActiveTab(tab); // Set the active tab state
        navigate(`/division/${divisionId}/${tab}`); // Update the URL with the new tab
    };

    // Ensure the tab contents are set only after data is successfully fetched
    useEffect(() => {
        if (data) {
            if (data.is_nationals === true) {
                setTabContents({
                    standings: <DivisionTournament divisionData={data}/>,
                    schedule: <DivisionSchedule divisionData={data}/>,
                    stats: <DivisionStatistics divisionID={divisionId} divisionName={data.name} divisionTeams={data.teams}/>,
                    rankings: <PowerRankings divisionData={data} />,
                    allstars: <AllStars divisionData={data} />,
                });
                setActiveTab(tab ? tab : "standings");
            } else if (data.uses_table === true) {
                setTabContents({
                    standings: <DivisionStandings divisionData={data}/>,
                    schedule: <DivisionSchedule divisionData={data}/>,
                    stats: <DivisionStatistics divisionID={divisionId} divisionName={data.name} divisionTeams={data.teams}/>,
                    // rankings: <PowerRankings />,
                });
                setActiveTab(tab ? tab : "standings");
            } else {
                setTabContents({
                    schedule: <DivisionSchedule divisionData={data}/>,
                    stats: <DivisionStatistics divisionID={divisionId} divisionTeams={data.teams}/>,
                });
                setActiveTab(tab ? tab : "schedule");
            }
        }
    }, [data, divisionId]);

    // Sync the active tab with the URL param when the URL changes (e.g., via back/forward navigation)
    useEffect(() => {
        if (tab) {
            setActiveTab(tab); // Set the active tab based on the URL param
        } else {
            setActiveTab("standings"); // Default to 'standings' tab if none is provided
        }
        console.log(tab, activeTab)
    }, [tab]);

    // Handle loading and error states
    if (isLoading)
        return (
            <Loading/>
        );

    if (error) return "An error has occurred: " + error.message;

    const renderTabContent = () => {
        return tabContents && tabContents[activeTab]
            ? tabContents[activeTab]
            : null;
    };

    // Render the active tab content
    return (
        <>
            <Helmet>
                <title>{data.name + " " + data.season} | BUIHA Division</title>
                <meta
                    name="description"
                    content={`Division page for ${data.name} ${data.season} including Standings, Schedule and Statistics`}
                />
                <link rel="canonical" href={`https://buiha.org/division/${divisionId}`}/>
                <link rel="icon" type="image/png" href="/favicon.png"/>
            </Helmet>

            <DivisionBanner
                activeTab={activeTab}
                handleTabClick={handleTabClick}
                div_data={data}
                doStandings={data.uses_table}
                doPowerRankings={data.is_nationals}
                doAllStars={data.has_allstars}
            />

            <Container>
                <PageSidebarContainer
                    children={
                        <>
                            <ContentContainer
                                children={<>{renderTabContent()}</>}
                                extraClasses={"mt-[16px] mb-4"}
                            />
                            <DivisionSidebar division={data}/>
                        </>
                    }
                />
            </Container>
        </>
    );
};

export default Division;
